import React from "react";
import { formatNumber, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Zap,
  Calendar,
  Clock,
  CheckCircle2,
  PlayCircle,
  PauseCircle,
  Layers,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  cleanProcessName,
  formatProcessDuration,
  DayGroup,
} from "../utils/processPlanningUtils";
import { formatProcessTime } from "../ProcessPlanningList";
import { ProcessStatusBadge } from "./ProcessStatusBadge";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
  type RowSelectionInstance,
} from "@/components/shared/DataTableRow";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";

interface ProcessDailyGroupedTableProps {
  groups: DayGroup[];
  /** @deprecated danh sách không còn link FK — giữ prop để tương thích */
  onNavigateToOrder?: (poCode: string) => void;
  onNavigateToSalesOrder?: (salesOrderCode: string) => void;
  onOpenLogForm: (item: any) => void;
  /** Bấm dòng → mở hộp thoại điều độ của chính công đoạn đó */
  onOpenMachineModal: (item: any) => void;
  rowSelection?: RowSelectionInstance<string>;
}

export function ProcessDailyGroupedTable({
  groups,
  onOpenLogForm,
  onOpenMachineModal,
  rowSelection,
}: ProcessDailyGroupedTableProps) {
  // State mở rộng/thu gọn từng ngày
  const [collapsedDates, setCollapsedDates] = React.useState<Set<string>>(new Set());

  const toggleCollapse = (dateKey: string) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  // Bấm dòng mở hộp thoại công đoạn; không có link sang bảng cha (LSX/đơn/khách/SP/máy) trong danh sách
  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const isCollapsed = collapsedDates.has(group.dateKey);
        const sub = group.subtotal;

        return (
          <div
            key={group.dateKey}
            className="rounded-xl border border-slate-200 bg-white shadow-2xs overflow-hidden"
          >
            {/* Header Khối Ngày */}
            <div
              data-group-row=""
              onClick={() => toggleCollapse(group.dateKey)}
              className={cn(
                "px-4 py-2.5 flex items-center justify-between gap-3 border-b cursor-pointer transition-colors select-none overflow-x-auto whitespace-nowrap",
                group.dateKey === "NO_DATE"
                  ? "bg-slate-100 border-slate-200 text-slate-700"
                  : "bg-teal-50/80 hover:bg-teal-100/70 border-teal-200 text-teal-900"
              )}
            >
              <div className="flex items-center gap-3 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-600 hover:text-teal-700"
                >
                  {isCollapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-teal-700" />
                  <span className="font-bold text-sm text-slate-900 tracking-tight">
                    {group.displayDate}
                  </span>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/90 border border-teal-200 text-teal-800 shadow-2xs">
                  {sub.itemCount} công đoạn
                </span>
                {sub.totalDurationHours > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 shadow-2xs">
                    ⏳ {sub.totalDurationHours} giờ định mức
                  </span>
                )}
                {sub.totalStandardQty > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-slate-700">
                    📦 {formatNumber(sub.totalStandardQty)} sp yêu cầu
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs font-mono shrink-0">
                <span className="text-emerald-700 font-bold">
                  Đạt: {formatNumber(sub.totalActualOk)}
                </span>
                {sub.totalActualNg > 0 && (
                  <span className="text-rose-600 font-bold">
                    Lỗi: {formatNumber(sub.totalActualNg)}
                  </span>
                )}
                <span className="text-slate-700">
                  Còn: <strong>{formatNumber(sub.totalRemainingQty)}</strong>
                </span>
                <span className="text-slate-600 font-semibold bg-white px-2 py-0.5 rounded border border-slate-200">
                  Tiến độ: {sub.progressPercent}%
                </span>
              </div>
            </div>

            {/* Bảng Chi Tiết Trong Ngày */}
            {!isCollapsed && (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-xs border-collapse min-w-[1020px]">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 select-none">
                    <tr>
                      <th
                        className="px-2 py-2 text-left font-bold w-[13.5%] sticky left-0 z-30 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)] whitespace-nowrap"
                        style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                      >
                        <div className="flex items-center gap-2">
                          <DataTableSelectAllCheckbox rowSelection={rowSelection} asChild />
                          <span>Mã Lệnh SX</span>
                        </div>
                      </th>
                      <th className="px-2 py-2 text-left font-bold w-[11%] whitespace-nowrap">Đơn Hàng / Khách</th>
                      <th className="px-2 py-2 text-left font-bold w-[10.5%] whitespace-nowrap">Sản Phẩm</th>
                      <th className="px-2 py-2 text-left font-bold w-[9.5%] whitespace-nowrap">Công Đoạn</th>
                      <th className="px-2 py-2 text-left font-bold w-[8%] whitespace-nowrap">Máy / Thợ</th>
                      <th className="px-2 py-2 text-left font-bold w-[10.5%] whitespace-nowrap">Lịch & Hạn</th>
                      <th className="px-2 py-2 text-left font-bold w-[10%] whitespace-nowrap">Định Mức</th>
                      <th className="px-2 py-2 text-left font-bold w-[9%] whitespace-nowrap">Tiến Độ</th>
                      <th className="px-1 py-2 text-center font-bold w-[8%] whitespace-nowrap">Trạng Thái</th>
                      <th
                        className="px-2 py-2 text-right font-bold w-[10%] sticky right-0 z-30 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)] whitespace-nowrap"
                        style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                      >
                        Thao Tác
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {group.items.map((item: any, idx: number) => {
                      const isEven = idx % 2 === 0;
                      const solidRowBg = isEven ? "#ffffff" : "#f8fafc";
                      const cleanName = cleanProcessName(item.name, item.stepId, item.processGroupName);
                      const durationStr = formatProcessDuration(
                        item.estDurationHours,
                        item.plannedTotalTime,
                        item.plannedSetupTime,
                        item.plannedSpeed
                      );

                      return (
                        <DataTableRow
                          key={item.id}
                          id={item.id}
                          rowSelection={rowSelection}
                          onClick={() => onOpenMachineModal(item)}
                          className="hover:bg-teal-50/40 transition-colors group"
                        >
                          {/* Sticky Col 1: PO Code & Status */}
                          <td
                            className="px-2 py-2 align-middle sticky left-0 z-20 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)] overflow-hidden"
                            style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                          >
                            <div className="flex items-start gap-2">
                            {rowSelection && (
                              <span className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                                <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} asChild />
                              </span>
                            )}
                            <div className="flex flex-col gap-1 min-w-0">
                              <span className="font-mono font-bold text-teal-700 whitespace-nowrap">
                                {item.poCode || item.productionOrderId.substring(0, 8)}
                              </span>
                              {item.poStatus && !["RELEASED", "IN_PROGRESS", "COMPLETED"].includes(item.poStatus) && (
                                <span className="text-[10px] font-medium px-1.5 py-0.2 rounded bg-amber-50 text-amber-700 border border-amber-200/60 w-fit whitespace-nowrap">
                                  Chưa ban hành
                                </span>
                              )}
                            </div>
                            </div>
                          </td>

                          {/* Order & Customer */}
                          <td className="px-2 py-2 align-middle overflow-hidden">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-mono font-semibold text-slate-800 whitespace-nowrap">
                                {item.salesOrderCode || "—"}
                              </span>
                              <span
                                className="text-[11px] text-muted-foreground truncate max-w-[125px]"
                                title={item.customerName}
                              >
                                {item.customerName || "Khách lẻ"}
                              </span>
                            </div>
                          </td>

                          {/* Product */}
                          <td className="px-2 py-2 align-middle">
                            <div className="flex items-center gap-2">
                              <ProductionThumbnailCell
                                imageUrl={item.productImage || item.productionOrder?.product?.image}
                                layoutUrl={item.layoutUrl || item.productionOrder?.printLayouts?.[0]?.fileUrl}
                                title={item.productName || item.poCode}
                                subtitle={item.poCode}
                                type="auto"
                                size={30}
                                className="shrink-0"
                              />
                              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                <span
                                  className="font-semibold text-slate-800 line-clamp-1"
                                  title={item.productName}
                                >
                                  {item.productName}
                                </span>
                                {item.productCode && (
                                  <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[120px]">
                                    {item.productCode}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Routing Step (Clean Name - Rule 6) */}
                          <td className="px-2 py-2 align-middle">
                            <div className="flex items-center gap-1.5">
                              <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-slate-200 text-slate-800 shrink-0">
                                B{item.stepOrder}
                              </span>
                              <span className="font-semibold text-slate-900">
                                {cleanName}
                              </span>
                            </div>
                          </td>

                          {/* Machine & Operator */}
                          <td className="px-2 py-2 align-middle">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium text-slate-800 truncate max-w-[110px]" title={item.machineName}>
                                {item.machineName}
                              </span>
                              <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">
                                Thợ: {item.operatorName}
                              </span>
                            </div>
                          </td>

                          {/* Plan & Deadline */}
                          <td className="px-2 py-2 align-middle whitespace-nowrap">
                            <div className="flex flex-col gap-0.5 text-[11px] font-mono">
                              <span className="text-slate-500">
                                Từ: <strong className="text-slate-700">{formatProcessTime(item.plannedStartDate)}</strong>
                              </span>
                              <div className="flex items-center gap-1">
                                <span
                                  className={cn(
                                    "font-semibold",
                                    item.isOverdue ? "text-rose-600 font-bold" : "text-slate-700"
                                  )}
                                >
                                  Hạn: {formatProcessTime(item.plannedEndDate)}
                                </span>
                                {item.isOverdue && (
                                  <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                                    Trễ
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Số lượng yêu cầu & Thời gian định mức */}
                          <td className="px-2 py-2 align-middle whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-mono font-bold text-indigo-700 text-xs">
                                {formatNumber(item.standardQuantity)} {item.productionUnit || "tờ"}
                              </span>
                              <div className="flex items-center gap-1 text-[10px] text-slate-600">
                                <Clock className="w-3 h-3 text-slate-400 shrink-0" />
                                <strong className="font-medium">{durationStr}</strong>
                              </div>
                            </div>
                          </td>

                          {/* Actual Ok / Progress */}
                          <td className="px-2 py-2 align-middle">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center justify-between text-[11px] font-mono">
                                <span className="text-emerald-600 font-bold">
                                  Đạt: {formatNumber(item.actualQtyOk)}
                                </span>
                                {item.actualQtyNg > 0 && (
                                  <span className="text-rose-600 font-medium">
                                    Lỗi: {formatNumber(item.actualQtyNg)}
                                  </span>
                                )}
                                <span className="text-slate-500 font-semibold">
                                  {item.progressPercent}%
                                </span>
                              </div>
                              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full rounded-full transition-all duration-300",
                                    item.progressPercent >= 100
                                      ? "bg-emerald-500"
                                      : item.progressPercent > 50
                                      ? "bg-teal-500"
                                      : "bg-blue-500"
                                  )}
                                  style={{ width: `${Math.min(100, item.progressPercent)}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground">
                                Còn lại: <strong className="text-slate-700">{formatNumber(item.remainingQty)}</strong>
                              </span>
                            </div>
                          </td>

                          {/* Step Status Badge */}
                          <td className="px-1.5 py-2 align-middle text-center whitespace-nowrap">
                            <ProcessStatusBadge status={item.status} />
                          </td>

                          {/* Sticky Right: Actions */}
                          <td
                            className="px-2 py-2 align-middle text-right sticky right-0 z-20 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                            style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                className="h-7 px-2 text-[11px] font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-xs flex items-center gap-1 cursor-pointer whitespace-nowrap"
                                onClick={() => onOpenLogForm(item)}
                                title="Ghi nhận sản lượng cho công đoạn này"
                              >
                                <Zap className="w-3 h-3 fill-amber-300 text-amber-300 shrink-0" />
                                <span>Ghi Nhật Ký</span>
                              </Button>
                            </div>
                          </td>
                        </DataTableRow>
                      );
                    })}

                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
